// INSERT-shape extractor — pulls insertTarget / insertColumns /
// insertValuesRows / lifted SELECT shape / onConflict / returning.
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: Query Structure Extractor — DML.

import type { IdentifierRef, ParseOptions, SqlRange, StatementInfo } from '../../sql-grammar/model';
import { rangeFromOffsets } from '../../sql-grammar/document/lineIndex';
import { readIdentifier } from '../scope/identifiers';
import { skipContextFor } from '../classify/tokenSkip';
import type { BooleanFragment, OnConflictSpec, OnDuplicateSpec } from '../types';

import { findTopLevelCommas, scanTopLevelKeywords, skipParen, type TopKeyword } from './scanner';
import { extractSelectStructure, type SelectExtractCtx } from './extractQueryStructure.select';
import { parseBooleanFragments } from './extractQueryStructure.predicates';
import { parseSelectItems } from './extractQueryStructure.selectList';
import {
  buildColumnRef, parseAssignmentsRange, readColumnListInner, readDmlTarget,
  type MutableQueryStructure,
} from './extractQueryStructure.dml.shared';

// eslint-disable-next-line complexity, max-lines-per-function
export function extractInsertShape(
  ctx: SelectExtractCtx,
  start: number,
  end: number,
): MutableQueryStructure {
  const { source, options } = ctx;
  const kws = scanTopLevelKeywords(source, start, end, options);
  const intoIdx = kws.findIndex((k) => k.upper === 'INTO');
  if (intoIdx < 0) return {};
  const insertTarget = readDmlTarget(ctx, kws[intoIdx].end, end);
  if (!insertTarget) return {};

  const colsResult = readInsertColumns(ctx, insertTarget.endOffset, end);

  const out: MutableQueryStructure = { insertTarget: insertTarget.item };
  if (colsResult.columns.length > 0) out.insertColumns = colsResult.columns;

  const after = colsResult.endOffset;
  const tailKws = kws.filter((k) => k.start >= after);

  const valuesKw = tailKws.find((k) => k.upper === 'VALUES');
  const selectKw = tailKws.find((k) => k.upper === 'SELECT');
  // Only the `ON` that begins an `ON CONFLICT` (PG/SQLite) or `ON DUPLICATE`
  // (MySQL) clause is an INSERT-level bound. A lifted SELECT's `JOIN … ON …`
  // sits at the same top level, so matching a bare `ON` truncates the SELECT
  // before its JOIN predicate (F18/F43) — hence the two-arm next-token match.
  const onConflictKw = tailKws.find((k, i) => k.upper === 'ON' && tailKws[i + 1]?.upper === 'CONFLICT');
  const onDuplicateKw = tailKws.find((k, i) => k.upper === 'ON' && tailKws[i + 1]?.upper === 'DUPLICATE');
  const returningKw = tailKws.find((k) => k.upper === 'RETURNING');
  // Earliest INSERT-level tail keyword caps the VALUES rows / source-SELECT
  // body. ON CONFLICT / ON DUPLICATE / RETURNING are mutually exclusive per
  // engine (only ON CONFLICT + RETURNING can co-occur, on PG), so the min of
  // their start offsets is the correct body bound. This preserves the old
  // `onKw?.start ?? returningKw?.start ?? end` result for existing on-conflict
  // shapes byte-for-byte while also capping at an `ON DUPLICATE` tail.
  const bodyEnd = Math.min(
    onConflictKw?.start ?? end,
    onDuplicateKw?.start ?? end,
    returningKw?.start ?? end,
  );

  if (valuesKw && (!selectKw || valuesKw.start < selectKw.start)) {
    const parsed = parseValuesRows(source, options, valuesKw.end, bodyEnd);
    if (parsed.rows.length > 0) {
      out.insertValuesRows = parsed.rows;
      out.insertValueRowRanges = parsed.ranges.map(
        (r) => rangeFromOffsets(ctx.lineIdx, r.start, r.end),
      );
    }
  } else if (selectKw) {
    const subStmt: StatementInfo = {
      id: `insert-select-${selectKw.start}-${bodyEnd}`,
      range: rangeFromOffsets(ctx.lineIdx, selectKw.start, bodyEnd),
      kind: 'select', risk: 'read', readsFrom: [], writesTo: [], schemaEffects: [],
    };
    const sub = extractSelectStructure(ctx.buffer, subStmt);
    liftSelectFields(sub, out);
  }

  if (onConflictKw) {
    const conflict = tailKws.find((k) => k.upper === 'CONFLICT' && k.start > onConflictKw.start);
    if (conflict) {
      const conflictEnd = returningKw?.start ?? end;
      const oc = parseOnConflict(ctx, conflict.end, conflictEnd, onConflictKw.start);
      if (oc) out.onConflict = oc;
    }
  }

  if (onDuplicateKw) {
    const od = parseOnDuplicate(ctx, onDuplicateKw, returningKw?.start ?? end);
    if (od) out.onDuplicate = od;
  }

  if (returningKw) {
    const retItems = parseSelectItems(ctx, returningKw.end, end);
    if (retItems.length > 0) out.returning = retItems;
  }

  return out;
}

// Parse a MySQL `ON DUPLICATE KEY UPDATE col = expr, …` tail. `onKw` is the
// `ON` keyword; we walk the `DUPLICATE KEY UPDATE` run and hand the remainder
// to the shared assignment parser (reused from UPDATE … SET / ON CONFLICT).
function parseOnDuplicate(
  ctx: SelectExtractCtx,
  onKw: TopKeyword,
  end: number,
): OnDuplicateSpec | undefined {
  const { source, options, lineIdx } = ctx;
  let i = onKw.end;
  for (const expected of ['DUPLICATE', 'KEY', 'UPDATE']) {
    while (i < end && /\s/.test(source[i])) i++;
    const word = readIdentifier(source, i, end, options);
    if (!word || word.value.toUpperCase() !== expected) return undefined;
    i = word.end;
  }
  const assignments = parseAssignmentsRange(source, options, lineIdx, i, end);
  if (assignments.length === 0) return undefined;
  const rangeEnd = assignments[assignments.length - 1].range.endOffset;
  return { assignments, range: rangeFromOffsets(lineIdx, onKw.start, rangeEnd) };
}

function liftSelectFields(
  sub: ReturnType<typeof extractSelectStructure>,
  out: MutableQueryStructure,
): void {
  if (sub.distinct !== undefined) out.distinct = sub.distinct;
  if (sub.selectItems) out.selectItems = sub.selectItems;
  if (sub.fromItems) out.fromItems = sub.fromItems;
  if (sub.joins) out.joins = sub.joins;
  if (sub.where) out.where = sub.where;
  if (sub.groupBy) out.groupBy = sub.groupBy;
  if (sub.having) out.having = sub.having;
  if (sub.orderBy) out.orderBy = sub.orderBy;
  if (sub.limit) out.limit = sub.limit;
  if (sub.compoundBranches) out.compoundBranches = sub.compoundBranches;
}

function readInsertColumns(
  ctx: SelectExtractCtx,
  start: number,
  end: number,
): { columns: readonly IdentifierRef[]; endOffset: number } {
  const { source, options } = ctx;
  let i = start;
  while (i < end && /\s/.test(source[i])) i++;
  if (source[i] !== '(') return { columns: [], endOffset: start };
  const close = skipParen(source, i, end, skipContextFor(options));
  const cols = readColumnListInner(ctx, i + 1, close > 0 ? close - 1 : end);
  return { columns: cols, endOffset: close };
}

function parseValuesRows(
  source: string,
  options: ParseOptions,
  start: number,
  end: number,
): { rows: readonly (readonly string[])[]; ranges: readonly { start: number; end: number }[] } {
  const ctx = skipContextFor(options);
  const rows: string[][] = [];
  const ranges: { start: number; end: number }[] = [];
  let i = start;
  while (i < end) {
    while (i < end && /\s/.test(source[i])) i++;
    if (i >= end || source[i] !== '(') break;
    const rowStart = i;
    const close = skipParen(source, i, end, ctx);
    const innerStart = i + 1;
    const innerEnd = close > 0 ? close - 1 : end;
    const commas = findTopLevelCommas(source, innerStart, innerEnd, options);
    const cells: string[] = [];
    let s = innerStart;
    for (const c of commas) { cells.push(source.slice(s, c).trim()); s = c + 1; }
    cells.push(source.slice(s, innerEnd).trim());
    rows.push(cells);
    ranges.push({ start: rowStart, end: close });
    i = close;
    while (i < end && /\s/.test(source[i])) i++;
    if (source[i] !== ',') break;
    i++;
  }
  return { rows, ranges };
}


// eslint-disable-next-line complexity, max-lines-per-function
function parseOnConflict(
  ctx: SelectExtractCtx,
  start: number,
  end: number,
  onStart: number,
): OnConflictSpec | undefined {
  const { source, options, lineIdx } = ctx;
  let i = start;
  while (i < end && /\s/.test(source[i])) i++;

  let target: readonly IdentifierRef[] | undefined;
  let targetRange: SqlRange | undefined;
  if (source[i] === '(') {
    const close = skipParen(source, i, end, skipContextFor(options));
    const cols = readColumnListInner(ctx, i + 1, close > 0 ? close - 1 : end);
    if (cols.length > 0) target = cols;
    // `close` is one past the closing `)`, so [i, close) spans `(…)` inclusive.
    if (close > 0) targetRange = rangeFromOffsets(lineIdx, i, close);
    i = close;
  }

  // Assemble the spec + additive byte ranges. `range` runs from the `ON`
  // keyword through the last content byte (`contentEnd`); `actionRange` (when
  // present) is passed by each caller. Trailing whitespace before a following
  // RETURNING / statement end is left OUT of `range`.
  const finish = (
    spec: OnConflictSpec,
    contentEnd: number,
    actionRange?: SqlRange,
  ): OnConflictSpec => ({
    ...spec,
    range: rangeFromOffsets(lineIdx, onStart, contentEnd),
    ...(targetRange ? { targetRange } : {}),
    ...(actionRange ? { actionRange } : {}),
  });

  while (i < end && /\s/.test(source[i])) i++;

  const doIdent = readIdentifier(source, i, end, options);
  if (!doIdent || doIdent.value.toUpperCase() !== 'DO') {
    return target ? finish({ target, action: 'do-nothing' }, i > start ? i : end) : undefined;
  }
  let p = doIdent.end;
  while (p < end && /\s/.test(source[p])) p++;
  const actionIdent = readIdentifier(source, p, end, options);
  if (!actionIdent) return target ? finish({ target, action: 'do-nothing' }, doIdent.end) : undefined;
  const actionUpper = actionIdent.value.toUpperCase();
  const actionRange = rangeFromOffsets(lineIdx, doIdent.start, actionIdent.end);
  if (actionUpper === 'NOTHING') {
    return finish({ ...(target ? { target } : {}), action: 'do-nothing' }, actionIdent.end, actionRange);
  }
  if (actionUpper !== 'UPDATE') return undefined;

  let q = actionIdent.end;
  while (q < end && /\s/.test(source[q])) q++;
  const setIdent = readIdentifier(source, q, end, options);
  if (!setIdent || setIdent.value.toUpperCase() !== 'SET') {
    return finish({ ...(target ? { target } : {}), action: 'do-update' }, actionIdent.end, actionRange);
  }
  const setStart = setIdent.end;
  const tailKws = scanTopLevelKeywords(source, setStart, end, options);
  const whereKw = tailKws.find((k) => k.upper === 'WHERE');
  const setEnd = whereKw?.start ?? end;
  const assignments = parseAssignmentsRange(source, options, lineIdx, setStart, setEnd);
  let where: readonly BooleanFragment[] | undefined;
  if (whereKw) where = parseBooleanFragments(ctx, whereKw.end, end);
  let contentEnd = actionIdent.end;
  if (assignments.length) contentEnd = assignments[assignments.length - 1].range.endOffset;
  if (where && where.length) contentEnd = where[where.length - 1].range.endOffset;
  return finish({
    ...(target ? { target } : {}),
    action: 'do-update',
    ...(assignments.length ? { updateAssignments: assignments } : {}),
    ...(where ? { where } : {}),
  }, contentEnd, actionRange);
}

// Re-export for the top-level dispatcher.
export { buildColumnRef };
